/* React Imports */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useNavigate } from 'react-router-v6';

/* Styling Imports */
import './scss/chatbot.scss';
import { Row, Col, Button, Card, Popconfirm, Skeleton } from 'antd';
import {
  PdfIcon,
  SpreadsheetsIcon,
  TextSourceIcon,
  WordDocIcon,
} from 'assets/icons';
import { DeleteOutlined } from '@ant-design/icons';
const { Meta } = Card;

/* API Services */
import { getUserBots, deleteChatbot } from 'api-services/container';
import { FREE_PLAN_LOOKUP } from 'utilities/constants';

/* Hooks */
import useLoading from 'hooks/use-loading';
import useDecodeToken from 'hooks/use-decode-token';

/* Event Emitter */
import eventEmitter from 'utilities/event-emitter';
import AuthService from 'api-services/auth-service';
import TextDocumentIcon from 'assets/icons/TextDocumentIcon';
import WebSiteIcon from 'assets/icons/WebSiteIcon';

function ChatBotList() {
  const navigate = useNavigate();

  /* Custom Hooks */
  const { setLoading } = useLoading();
  const { userId, planId, botsAllowed } = useDecodeToken();

  /* Component States */
  const [botList, setBotList] = useState([]);
  const [skeleton, setSkeleton] = useState(true);

  /* Getting list of current user's bots */
  const getBotsData = async () => {
    try {
      setSkeleton(true);
      const result = await getUserBots(userId);
      if (result.status === 200) {
        //success handling
        setBotList(result.data);
        if (result.data.length === 0) chatbotHandler();
      }
    } catch (e) {
      //failure handling
      console.log(e);
    } finally {
      setSkeleton(false);
    }
  };

  /* Chatbot Creation */
  const chatbotHandler = () => {
    navigate('/add-data-source', { state: { hideSideBar: false } });
  };

  /* Getting Icon for Bots */
  const getBotTypeIcon = (botType) => {
    switch (botType) {
    case 'pdf':
      return <PdfIcon />;

    case 'docx':
    case 'doc':
      return <WordDocIcon />;

    case 'xlsx':
    case 'xls':
    case 'csv':
      return <SpreadsheetsIcon />;
      
    case 'text':
      return <TextSourceIcon />;
    case 'website':
      return <WebSiteIcon />;
    default:
      return <TextDocumentIcon  />;
    }
  };

  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    const updateParam = queryParams.get('update');

    if (updateParam === 'true') {
      const refresh = async () => {
        const token = await AuthService.refreshToken();
        eventEmitter.emit('userInfoUpdated', token);
        queryParams.delete('update');

        const timeoutId = setTimeout(() => {
          if (userId) getBotsData(); 
        }, 1000);

        // Clear the timeout when the component is unmounted
        return () => clearTimeout(timeoutId);
      };
      refresh();
    } else {
      if (userId) getBotsData(); //getting all user bots
    }
  }, [userId]);

  /* UI */
  return (
    <div className="chatbotlist-wrapper">
      <div className="card-header">
        <div className="card-header-title">
          <h3 className="page-title">My Chatbots</h3>
          {planId !== FREE_PLAN_LOOKUP && `(${botsAllowed} chatbots limit)`}
          {planId === FREE_PLAN_LOOKUP && <p>(1 Free chatbot limit)</p>}
        </div>
        <div className="card-header-action">
          <Button
            type="primary"
            onClick={chatbotHandler}
            disabled={
              (botList.length === botsAllowed) || skeleton
            }
          >
            New Chatbot
          </Button>
        </div>
      </div>
      <Row gutter={24}>
        {skeleton && <Skeleton active />}
        {!skeleton &&
          botList.map((bot) => (
            <Col span={6} key={bot.id}>
              <Card
                actions={[
                  <Popconfirm
                    title="Deleting Chatbot"
                    key={bot.id}
                    description="Are you sure to want to delete this chatbot? All the data associated with this chatbot will be lost."
                    onConfirm={async () => {
                      try {
                        setLoading(true);
                        const r = await deleteChatbot(bot.id);
                        if (r.status === 200) {
                          eventEmitter.emit('botSelected', {
                            id: bot.id,
                            clear: true
                          });
                          getBotsData();
                        }
                      } catch (e) {
                        console.log(e);
                      } finally {
                        setLoading(false);
                      }
                    }}
                    okText="Yes"
                    cancelText="No"
                  >
                    <DeleteOutlined onClick={() => {}} />
                  </Popconfirm>,
                ]}
              >
                <Link
                  to="/chatbot"
                  onClick={() => {
                    eventEmitter.emit('botSelected', bot);
                  }}
                >
                  <Meta
                    avatar={getBotTypeIcon(bot.bot_source_type)}
                    title={bot.bot_name}
                  />
                </Link>
              </Card>
            </Col>
          ))}
      </Row>
    </div>
  );
}

export default ChatBotList;
